import { createContext, use } from 'react'

/**
 * The eight themes, in the order the appearance settings list them.
 *
 * Only colour changes between them: layout, type, spacing, radius and shadow
 * geometry are identical, so a theme is nothing more than a `data-theme`
 * value that `globals.css` keys a token block on.
 *
 * `index.html` repeats this list in its pre-paint script, which runs before
 * any module can be imported. Change both together.
 */
export const THEMES = [
  'default',
  'ocean',
  'emerald',
  'indigo',
  'amber',
  'slate',
  'dark',
  'midnight',
] as const

export type Theme = (typeof THEMES)[number]

/** The themes whose surfaces are dark — native controls and the toaster follow. */
export const DARK_THEMES: ReadonlySet<Theme> = new Set<Theme>(['dark', 'midnight'])

/**
 * What a person picks: one of the themes, or `system` — the default theme by
 * day and the dark theme by night, as the device says. `system` is where
 * everyone starts: an all-day tool should not glare at someone whose whole
 * screen has gone dark.
 */
export const PREFERENCES = ['system', ...THEMES] as const

export type ThemePreference = (typeof PREFERENCES)[number]

/**
 * Kept as `theme`, the key the old light/dark toggle wrote. A stored `dark`
 * or `system` therefore carries straight over; `light` is not a theme any
 * more and falls back to the default.
 */
export const THEME_STORAGE_KEY = 'theme'

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || isTheme(value)
}

/** The theme a preference shows: `system` is the default theme or the dark one. */
export function resolveTheme(preference: ThemePreference, prefersDark: boolean): Theme {
  if (preference !== 'system') return preference
  return prefersDark ? 'dark' : 'default'
}

export const ThemeContext = createContext<{
  /** The theme on screen. */
  theme: Theme
  /** What was picked: a theme, or `system`. */
  preference: ThemePreference
  setTheme: (next: ThemePreference) => void
} | null>(null)

/**
 * Read and change the theme.
 *
 * Throws outside its provider rather than defaulting: a silent default is a
 * picker that appears to work and changes nothing.
 */
export function useTheme() {
  const context = use(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider')
  return context
}
